import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { MdDelete } from 'react-icons/md'
import type { ExamSelectedItem, OrderSelectedItem } from '../types'

type SelectableItem = ExamSelectedItem | OrderSelectedItem

const money = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })

interface SelectedItemsTableProps<T extends SelectableItem> {
    items: T[]
    setItems: Dispatch<SetStateAction<T[]>>
    /** Cabeçalho da primeira coluna ("Item", "Procedimento"). */
    itemColumnLabel: string
    /**
     * Coluna "Tipo" (o `catalogTypeName`) — só a pilha de procedimentos a mostra;
     * no exame ela ficaria vazia na maioria das linhas.
     */
    showTypeColumn?: boolean
    removeTitle: string
    /**
     * Controles de variação da linha, renderizados pela página (a cascata de preço
     * depende do parceiro/profissional escolhidos no agendamento).
     */
    renderVariationControls: (
        item: T,
        index: number,
        items: T[],
        setItems: Dispatch<SetStateAction<T[]>>
    ) => ReactNode
}

/**
 * Pilha de itens escolhidos do catálogo, com quantidade, subtotal e total.
 *
 * Exame e procedimento empilham vários itens do catálogo com a mesma mecânica —
 * a diferença é a coluna "Tipo" e os rótulos. Eram duas tabelas copiadas, e
 * corrigir uma sem a outra já deixou as duas divergentes.
 */
export default function SelectedItemsTable<T extends SelectableItem>({
    items,
    setItems,
    itemColumnLabel,
    showTypeColumn = false,
    removeTitle,
    renderVariationControls,
}: SelectedItemsTableProps<T>) {
    if (items.length === 0) return null

    const total = items.reduce(
        (sum, item) => sum + item.unitPrice * item.quantity,
        0
    )

    return (
        <div className="overflow-x-auto rounded-xl border border-base-300">
            <table className="table table-sm">
                <thead>
                    <tr>
                        <th>{itemColumnLabel}</th>
                        {showTypeColumn && <th>Tipo</th>}
                        <th className="text-right">Preço unit.</th>
                        <th className="w-24">Qtd.</th>
                        <th className="text-right">Subtotal</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((item, index) => (
                        <tr key={index}>
                            <td>
                                <span className="font-medium">
                                    {item.name}
                                    {item.variationName
                                        ? ` — ${item.variationName}`
                                        : ''}
                                </span>
                                {'partnerName' in item && item.partnerName && (
                                    <p className="mt-0.5 text-xs text-base-content/60">
                                        {item.partnerName}
                                    </p>
                                )}
                                {renderVariationControls(
                                    item,
                                    index,
                                    items,
                                    setItems
                                )}
                            </td>
                            {showTypeColumn && (
                                <td>
                                    <span className="text-xs text-base-content/70">
                                        {item.catalogTypeName || '-'}
                                    </span>
                                </td>
                            )}
                            <td className="text-right">
                                R$ {money(item.unitPrice)}
                            </td>
                            <td>
                                <input
                                    type="number"
                                    min="1"
                                    aria-label="Quantidade"
                                    className="input input-bordered input-xs w-16"
                                    value={item.quantity}
                                    onChange={e => {
                                        const quantity =
                                            parseInt(e.target.value) || 1
                                        setItems(prev =>
                                            prev.map((it, idx) =>
                                                idx === index
                                                    ? { ...it, quantity }
                                                    : it
                                            )
                                        )
                                    }}
                                />
                            </td>
                            <td className="text-right font-medium">
                                R$ {money(item.unitPrice * item.quantity)}
                            </td>
                            <td>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs text-error"
                                    title={removeTitle}
                                    onClick={() =>
                                        setItems(prev =>
                                            prev.filter((_, i) => i !== index)
                                        )
                                    }
                                >
                                    <MdDelete />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td
                            colSpan={showTypeColumn ? 4 : 3}
                            className="text-right font-semibold"
                        >
                            Total
                        </td>
                        <td className="text-right font-semibold">
                            R$ {money(total)}
                        </td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    )
}
